import { RootState } from 'redux/schemas';
import {
  CompletionStatus,
  MentoringProgramRole,
} from 'redux/schemas/models/mentoring-program-enrollments';
import {
  MentoringProgramGuideRole,
  MentoringProgramGuideRequirementType,
  SessionSequenceCriteria,
  ProfileCompletionCriteria,
  MentoringProgramMilestone,
  MentoringProgramGuide,
  ConnectionSessionCriteria,
} from 'redux/schemas/models/mentoring-program-guides';
import { getMentorshipProgramSessionIndex } from './mentorship-program-sessions';

export const getMentoringProgramGuides = (state: RootState) =>
  state.models.mentorshipProgramGuides;

export const getMentoringProgramGuideById =
  (guideId: number | null) => (state: RootState) => {
    if (!guideId) return null;
    return state.models.mentorshipProgramGuides[guideId] || null;
  };

const byRole =
  (role: MentoringProgramRole) => (guide: MentoringProgramGuide) => {
    if (guide.role === MentoringProgramGuideRole.MENTOR_AND_MENTEE) return true;
    return (
      (role === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR &&
        guide.role === MentoringProgramGuideRole.MENTOR) ||
      (role === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE &&
        guide.role === MentoringProgramGuideRole.MENTEE)
    );
  };

const bySessionOrConnectionSessionSequence =
  (sessionIndex: number) => (guide: MentoringProgramGuide) => {
    const req = guide.mentorshipProgramGuideRequirement;
    const { sequence } = req.criteria as SessionSequenceCriteria;
    const { upperLimit } = req.criteria as ConnectionSessionCriteria;
    if (sequence) {
      return (
        req.type === MentoringProgramGuideRequirementType.SESSION &&
        Array.isArray(sequence) &&
        sequence.includes(sessionIndex)
      );
    }
    if (upperLimit) {
      return (
        req.type === MentoringProgramGuideRequirementType.CONNECTION_SESSION &&
        upperLimit === sessionIndex
      );
    }
    return false;
  };

const byProfileCompletionOrReviewMilestone =
  (completionStatus: CompletionStatus) => (guide: MentoringProgramGuide) => {
    const req = guide.mentorshipProgramGuideRequirement;
    const { state, status } = req.criteria as ProfileCompletionCriteria;
    return (
      req.type === MentoringProgramGuideRequirementType.MILESTONE &&
      (state === MentoringProgramMilestone.PROFILE_COMPLETION || state === MentoringProgramMilestone.PROFILE_REVIEW) &&
      status === completionStatus
    );
  };

export const selectGuidesForSessionByRole =
  (role: MentoringProgramRole, sessionId: number) => (state: RootState) => {
    const sessionIndex = getMentorshipProgramSessionIndex(sessionId)(state);
    if (!sessionIndex) return [];
    const guides = Object.values(state.models.mentorshipProgramGuides);
    return guides.filter(byRole(role)).filter(bySessionOrConnectionSessionSequence(sessionIndex));
  };

export const selectGuidesForProfileCompletionOrReviewMilestoneByRole =
  (role: MentoringProgramRole, completionStatus: CompletionStatus) =>
  (state: RootState) => {
    const guides = Object.values(state.models.mentorshipProgramGuides);
    return guides
      .filter(byRole(role))
      .filter(byProfileCompletionOrReviewMilestone(completionStatus));
  };
